/**
 * Bootstrap de sesión cuando la usuaria aterriza desde landing
 * (daisies.mx) en agenda.daisies.mx ya autenticada.
 *
 * Dos vías:
 *   1. Cookies en .daisies.mx (producción) — las que escribe el landing
 *      tras WhatsApp verify o login. Mismos keys que ya usa consulta:
 *      daisies_user_id, daisies_auth_token, daisies_firebase_uid,
 *      daisies_user_profile, daisies_session_active.
 *   2. Query params en URL (dev / vercel preview, donde no compartimos
 *      dominio): u_id, u_tok, u_uid, u_email, u_name, u_phone.
 *
 * Si ya hay sesión en localStorage no tocamos nada — la sesión local es
 * la fuente de verdad. Solo bootstrap-eamos cuando localStorage está
 * vacío y traemos auth desde cookies o URL.
 *
 * Al terminar limpiamos los query params (replaceState) para no dejar
 * tokens visibles en la URL.
 */

import { saveUserSession } from './checkoutHelpers';

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function clearCookie(name) {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  const domain = host.endsWith('.daisies.mx') || host === 'daisies.mx' ? '.daisies.mx' : null;
  const parts = [`${name}=`, 'Max-Age=0', 'Path=/'];
  if (domain) parts.push(`Domain=${domain}`);
  document.cookie = parts.join('; ');
}

function readSessionFromCookies() {
  const userId = getCookie('daisies_user_id');
  const authToken = getCookie('daisies_auth_token');
  const firebaseUid = getCookie('daisies_firebase_uid');
  if (!userId || !authToken || !firebaseUid) return null;
  let profile = {};
  try {
    const profileStr = getCookie('daisies_user_profile');
    if (profileStr) profile = JSON.parse(profileStr);
  } catch (err) {
    console.warn('[sessionBootstrap] could not parse profile cookie:', err);
  }
  return { userId, authToken, firebaseUid, profile, source: 'cookie' };
}

function readSessionFromUrl() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const userId = params.get('u_id');
  const authToken = params.get('u_tok');
  const firebaseUid = params.get('u_uid');
  if (!userId || !authToken || !firebaseUid) return null;
  const profile = {
    email: params.get('u_email') || '',
    nombre: params.get('u_name') || '',
    telefono: params.get('u_phone') || '',
    phoneCountry: 'MX',
    phoneCode: '+52',
  };
  return { userId, authToken, firebaseUid, profile, source: 'url' };
}

function stripBootstrapParamsFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    ['u_id', 'u_tok', 'u_uid', 'u_email', 'u_name', 'u_phone'].forEach((k) =>
      url.searchParams.delete(k),
    );
    window.history.replaceState({}, '', url.toString());
  } catch {
    /* ignore */
  }
}

/**
 * Promueve la sesión disponible (cookies o URL params) a localStorage
 * vía saveUserSession. No-op si ya hay sesión local.
 *
 * Devuelve true si bootstrap-eó algo, false si no.
 */
export function bootstrapSessionFromBridge() {
  if (typeof window === 'undefined') return false;
  // Si ya hay sesión local, no tocamos.
  const existing = localStorage.getItem('daisies_user_id');
  if (existing) return false;

  const incoming = readSessionFromCookies() || readSessionFromUrl();
  if (!incoming) return false;

  try {
    saveUserSession(
      {
        userId: incoming.userId,
        authToken: incoming.authToken,
        firebaseUid: incoming.firebaseUid,
      },
      {
        nombre: incoming.profile.nombre || '',
        apellido: incoming.profile.apellido || '',
        email: incoming.profile.email || '',
        telefono: incoming.profile.telefono || '',
        phoneCountry: incoming.profile.phoneCountry || 'MX',
        phoneCode: incoming.profile.phoneCode || '+52',
        leadId: incoming.profile.leadId || null,
      },
    );
    // El flag de teléfono verificado se persiste aparte porque saveUserSession
    // sólo conoce los campos de perfil. La landing setea phoneVerified=true
    // tras un OTP exitoso; consulta lo respeta sin re-pedir verificación.
    if (incoming.profile.phoneVerified === true) {
      localStorage.setItem('daisies_phone_verified', '1');
    }
  } catch (err) {
    console.warn('[sessionBootstrap] saveUserSession failed:', err);
    return false;
  }

  if (incoming.source === 'url') {
    stripBootstrapParamsFromUrl();
  } else {
    // En cookie mode: el token vive 55min, no lo limpiamos (lo necesitamos).
    // El resto sí queda activo hasta su expiry natural.
  }
  return true;
}

export { clearCookie };
